import React, {
  useCallback,
  useState,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import FileUploadDropzone from 'src/components/documents/FileUploadDropzone';

// API hooks
import useWorkflowPreprocess from 'src/api/spreadsheet/workflow/useWorkflowPreprocess';
import useWorkflowMapFields from 'src/api/spreadsheet/workflow/useWorkflowMapFields';
import useWorkflowApplyMappings from 'src/api/spreadsheet/workflow/useWorkflowApplyMappings';
import useWorkflowGenerateWorkflow from 'src/api/spreadsheet/workflow/useWorkflowGenerateWorkflow';
import useParseCsvFromImage from 'src/api/spreadsheet/useParseCsvFromImage';
import useSubmitTransformedCSV from 'src/api/spreadsheet/useSubmitTransformedCSV';
import { useCheckOverlap } from 'src/api/spreadsheet/useCheckOverlap';
import useWorkflowExtractConstants from 'src/api/spreadsheet/workflow/useWorkflowExtractConstants';
import config from 'src/config';
import type {
  OverlappingInvoice,
  OverlapResolution,
  StartPendingUploadResponse,
} from '@erp/shared-types';
import { OverlapWarningModal } from 'src/components/spreadsheets/OverlapWarningModal';
import useSupplier from 'src/api/accounts-payable/useSupplier';
import useRebateAgreements from 'src/api/rebates/useRebateAgreements';
import { useCompanyBySupplier } from '@/api/core/companies/useCompanyBySupplier';
import { DEFAULT_CURRENCY_CODE } from '@erp/shared-types';
import {
  useCheckDuplicateDocuments,
  type DuplicateDocumentInfo,
} from 'src/api/core/documents/useCheckDuplicateDocuments';
import { DuplicateFileWarningModal } from './components/DuplicateFileWarningModal';

// Utils
// Note: applyFieldMappings moved to backend - kept as fallback only
import { applyFieldMappings } from './utils/dataTransformation';
import {
  generateCsvString,
  processExcelSheet,
  generateHierarchicalMessage,
  processSheetData,
  applyCurrencyConversion,
} from './utils/spreadsheetUtils';
import { HierarchicalParseOptions } from './utils/smartCellParser';
import { formatWorkflowExplanationsForChat } from './utils/formatExplanations';

// Types
import { SupportedGPTModels } from './types/spreadsheetTypes';
import type { WorkflowConstants } from './types/WorkflowConstants';
import {
  WorkflowPreprocessResponse,
  WorkflowMapFieldsResponse,
  WorkflowGenerateWorkflowResponse,
} from 'src/api/spreadsheet/workflow';
import { FieldMappings } from 'src/api/spreadsheet/workflow/useWorkflowMapFields';
import { REBATE_CSV_COLUMNS } from '@erp/shared-types';

// Conversation is maintained inside AIAssistantChatV2

// Adapted Components for individual states
import LinearWorkflowNavigator from './components/LinearWorkflowNavigator';
import PhaseZeroSheetSelection from './components/phases/PhaseZeroSheetSelection';
import PhaseOnePreprocessing from './components/phases/PhaseOnePreprocessing';
import PhaseTwoMapping, {
  PhaseTwoMappingHandle,
} from './components/phases/PhaseTwoMapping';
import PhaseThreeWorkflow from './components/phases/PhaseThreeWorkflow';
import AIAssistantChatV2, {
  AIAssistantChatV2Handle,
} from './components/AIAssistantChatV2';
import RawFilePreviewPopout from '../../../../components/common/RawFilePreviewPopout';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { useToast } from 'src/context/ToastContext';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  usePendingUpload,
  useStartPendingUpload,
  useUpdatePendingUpload,
} from '@/api/spreadsheet/usePendingUploads';

// Utils
import { sortTransformedData } from './utils';
import posthog from 'posthog-js';

import { WorkflowPhase } from './types/workflowTypes';
import { AI_MODELS } from '@erp/shared-types';
import LoadingSpinner from '@/components/common/LoadingSpinner';

// Guard to avoid duplicate image auto-parse under Strict Mode remounts
const autoParsedUploads = new Set<string>();

interface ConvertDataPageWorkflowProps {
  onBack?: () => void; // Optional - for returning to upload mode selection
}

const ConvertDataPageWorkflow: React.FC<ConvertDataPageWorkflowProps> = ({
  onBack,
}) => {
  // AI Assistant ref for appending messages from auto-runs
  const aiChatRef = useRef<AIAssistantChatV2Handle | null>(null);
  // Phase 2 mapping ref for alias check before phase transition
  const phaseTwoMappingRef = useRef<PhaseTwoMappingHandle | null>(null);
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Read pendingUploadId from URL search params (for email queue integration)
  const { pendingUploadId } = useSearch({ strict: false }) as {
    pendingUploadId?: string;
  };

  // Track if we've already processed the pending upload to avoid re-fetching
  const [pendingUploadProcessed, setPendingUploadProcessed] = useState(false);
  const [isLoadingPendingUpload, setIsLoadingPendingUpload] = useState(false);

  // Hooks for pending upload operations
  const { mutateAsync: startPendingUpload } = useStartPendingUpload();
  const { mutateAsync: updatePendingUpload } = useUpdatePendingUpload();

  // Model selection and validation errors
  // const [selectedModel, setSelectedModel] = useState<SupportedGPTModels>(
  //   import.meta.env.DEV ? AI_MODELS.GPT_4O : AI_MODELS.CLAUDE_OPUS_4_1,
  // );
  const [selectedModel, setSelectedModel] = useState<SupportedGPTModels>(
    AI_MODELS.CLAUDE_OPUS_4_1,
  );
  const [_validationErrors, setValidationErrors] = useState<string[]>([]);

  // Test mode toggle state (default to false)
  const [useTestMode, setUseTestMode] = useState<boolean>(false);

  // API hooks
  const { mutateAsync: preprocessWorkflow, isLoading: isPreprocessing } =
    useWorkflowPreprocess();
  const { mutateAsync: mapFieldsWorkflow, isLoading: isMapping } =
    useWorkflowMapFields();
  const { mutateAsync: applyMappingsWorkflow, isLoading: isApplyingMappings } =
    useWorkflowApplyMappings();
  const { mutateAsync: generateWorkflow, isLoading: isGeneratingWorkflow } =
    useWorkflowGenerateWorkflow();
  const { mutateAsync: parseCsvFromImage, isLoading: isParsing } =
    useParseCsvFromImage();
  // Banner error for persistent validation messages (e.g., submission failures)
  const [bannerError, setBannerError] = useState<string | null>(null);

  const { mutateAsync: submitTransformedCSV, isLoading: isSubmitting } =
    useSubmitTransformedCSV(setBannerError);
  const { mutateAsync: checkOverlap, isLoading: isCheckingOverlap } =
    useCheckOverlap();
  const { mutateAsync: extractConstants, isLoading: isExtractingConstants } =
    useWorkflowExtractConstants();

  // Overlap detection state
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [overlappingInvoices, setOverlappingInvoices] = useState<
    OverlappingInvoice[]
  >([]);
  const [totalAffectedLineItems, setTotalAffectedLineItems] = useState(0);
  // Store pending submission data for retry after overlap resolution
  const pendingSubmissionRef = useRef<{
    csvContent: string;
    fileName: string;
  } | null>(null);

  // Phase management
  const [currentPhase, setCurrentPhase] = useState<WorkflowPhase>(-1);

  // File and sheet data
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'csv' | 'xlsx' | 'image' | null>(
    null,
  );
  const [isImageReviewMode, setIsImageReviewMode] = useState(false);

  // Data - each phase stores its output for sequential transformation
  const [originalData, setOriginalData] = useState<unknown[][]>([]); // Raw uploaded file data
  const [preprocessedData, setPreprocessedData] = useState<unknown[][]>([]); // Phase 1 output
  const [mappedData, setMappedData] = useState<unknown[][]>([]); // Phase 2 output
  const [transformedData, setTransformedData] = useState<unknown[][]>([]); // Phase 3 output (final)

  // Currency conversion state
  const [preConversionData, setPreConversionData] = useState<unknown[][]>([]); // Store data before conversion for rate changes
  const [conversionApplied, setConversionApplied] = useState(false);
  const [appliedConversionRate, setAppliedConversionRate] = useState<
    number | undefined
  >(undefined); // Track the last APPLIED conversion rate (not just typed)

  // Phase results
  const [preprocessData, setPreprocessData] =
    useState<WorkflowPreprocessResponse | null>(null);
  const [mappingData, setMappingData] =
    useState<WorkflowMapFieldsResponse | null>(null);
  const [editedConstants, setEditedConstants] =
    useState<WorkflowConstants | null>(null);
  // Track the constants that were used when workflow was last successfully run
  const [appliedWorkflowConstants, setAppliedWorkflowConstants] =
    useState<WorkflowConstants | null>(null);
  // Fetch supplier once when supplierId is available and keep the name locally
  const [supplierName, setSupplierName] = useState<string | undefined>(
    undefined,
  );
  const { data: supplierResp } = useSupplier(
    (editedConstants as WorkflowConstants | null)?.supplierId,
    undefined,
    Boolean((editedConstants as WorkflowConstants | null)?.supplierId),
  );
  useEffect(() => {
    if (supplierResp?.supplier?.name) {
      setSupplierName(supplierResp.supplier.name);
    }
  }, [supplierResp]);

  // Fetch company to get default currency when supplier is selected/changed
  // IMPORTANT: Currency now comes from Company.metadata.defaultCurrency
  // This ensures currency updates reactively when user changes supplier in constants panel
  const currentSupplierId = (editedConstants as WorkflowConstants | null)
    ?.supplierId;
  const currentCurrency = (editedConstants as WorkflowConstants | null)
    ?.currency;

  const { data: supplierCompany } = useCompanyBySupplier(currentSupplierId);

  // Track previous supplier ID to detect supplier changes
  const prevSupplierIdRef = useRef<string | undefined>(undefined);

  // Update currency when supplier changes (not when currency changes)
  // This allows users to manually override the currency without it resetting
  useEffect(() => {
    if (currentSupplierId && supplierCompany) {
      const defaultCurrency =
        supplierCompany.metadata?.defaultCurrency || DEFAULT_CURRENCY_CODE;

      // Only auto-update currency if:
      // 1. The supplier has actually changed (not the currency)
      // 2. The new currency is different from the default
      const supplierChanged = prevSupplierIdRef.current !== currentSupplierId;

      if (supplierChanged && currentCurrency !== defaultCurrency) {
        setEditedConstants((prev) =>
          prev ? { ...prev, currency: defaultCurrency } : null,
        );
      }

      // Update the previous supplier ID after processing
      prevSupplierIdRef.current = currentSupplierId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierCompany, currentSupplierId]);

  // Fetch product groups for supplier to ensure at least one exists
  const supplierIdForGroups =
    (editedConstants as WorkflowConstants | null)?.supplierId || '';
  // Fetch rebate agreements for supplier to check for any active
  const { data: supplierAgreements } = useRebateAgreements(
    { supplierId: supplierIdForGroups },
    Boolean(supplierIdForGroups),
  );
  const [editedMappings, setEditedMappings] = useState<FieldMappings>({
    divisionMappings: {
      sourceColumnNames: [],
      targetColumnName: '',
      mappings: {},
      separator: '|||',
    },
    productMappings: {
      sourceColumnNames: [],
      targetColumnName: '',
      mappings: {},
      separator: '|||',
    },
    dateMappings: {
      sourceColumnNames: [],
      targetColumnName: '',
      mappings: {},
      separator: '|||',
    },
  });

  // Track the last successfully applied mappings to detect real changes
  const [lastAppliedMappings, setLastAppliedMappings] =
    useState<FieldMappings | null>(null);

  const [workflowData, setWorkflowData] =
    useState<WorkflowGenerateWorkflowResponse | null>(null);

  // Conversation is maintained by AIAssistantChatV2

  // Loading states
  const [isLoadingFileProcessing, setIsLoadingFileProcessing] = useState(false);
  const [isLoadingTransformedData, setIsLoadingTransformedData] =
    useState(false);
  const [isLoadingMappedData, setIsLoadingMappedData] = useState(false);
  const [isLoadingFinalData, setIsLoadingFinalData] = useState(false);
  const [isApplyingMappingsLocal, setIsApplyingMappingsLocal] = useState(false);

  // Error states
  const [fileProcessingError, setFileProcessingError] = useState<string | null>(
    null,
  );

  // UI state removed - now managed internally by SpreadsheetViewers
  const [transformError, setTransformError] = useState<string | null>(null);
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(false);

  // Workflow error handling states
  const [preprocessingWorkflowError, setPreprocessingWorkflowError] = useState<
    string | undefined
  >(undefined);
  const [preprocessingFailedStepId, setPreprocessingFailedStepId] = useState<
    string | undefined
  >(undefined);
  const [workflowGenerationError, setWorkflowGenerationError] = useState<
    string | undefined
  >(undefined);
  const [workflowGenerationFailedStepId, setWorkflowGenerationFailedStepId] =
    useState<string | undefined>(undefined);

  // Submit confirmation dialog
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [isDetectingConstants, setIsDetectingConstants] = useState(false);
  const constantsAutoTriggeredRef = useRef(false);

  // Duplicate file warning state
  const [duplicateWarningOpen, setDuplicateWarningOpen] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateDocumentInfo[]>(
    [],
  );
  const [pendingPhaseChange, setPendingPhaseChange] = useState<number | null>(
    null,
  );

  // Phase 3 validation: required columns and non-empty rows
  const requiredColumns = useMemo(
    () => [
      REBATE_CSV_COLUMNS.DATE,
      REBATE_CSV_COLUMNS.DIVISION_NAME,
      REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME,
      REBATE_CSV_COLUMNS.VALUE,
    ],
    [],
  );

  const phaseThreeValidation = useMemo(() => {
    // Use transformedData as the final dataset to validate
    const data = transformedData;
    if (!Array.isArray(data) || data.length === 0) {
      return {
        valid: false,
        reason: 'No transformed data to submit.',
      } as const;
    }

    const headerRow = (data[0] || []) as unknown[];
    const headerToIndex = new Map<string, number>();
    headerRow.forEach((cell, idx) => {
      const key = String(cell || '').trim();
      if (key) headerToIndex.set(key, idx);
    });

    // Check required columns exist
    for (const col of requiredColumns) {
      if (!headerToIndex.has(col)) {
        return {
          valid: false,
          reason: `Missing required column: ${col}`,
        } as const;
      }
    }

    // Check no data row has empty required values
    for (let r = 1; r < data.length; r += 1) {
      const row = data[r] as unknown[];
      for (const col of requiredColumns) {
        const idx = headerToIndex.get(col);
        const value = idx !== undefined ? row?.[idx] : undefined;
        const str = String(value ?? '').trim();
        if (str.length === 0) {
          return {
            valid: false,
            reason: `Row ${r} has empty value for required column "${col}"`,
          } as const;
        }
      }
    }

    return { valid: true, reason: '' } as const;
  }, [transformedData, requiredColumns]);

  // Unknowns detection helpers
  const containsUnknown = React.useCallback((data: unknown[][]): boolean => {
    try {
      for (const row of data || []) {
        for (const cell of row || []) {
          if (
            typeof cell === 'string' &&
            cell.trim().toLowerCase() === 'unknown'
          ) {
            return true;
          }
        }
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const hasUnknownInMapped = React.useMemo(
    () => containsUnknown(mappedData),
    [mappedData, containsUnknown],
  );

  const hasUnappliedChanges =
    !!lastAppliedMappings &&
    JSON.stringify(editedMappings) !== JSON.stringify(lastAppliedMappings);

  // Handler for resetting mappings to last applied state
  const handleResetMappings = useCallback(() => {
    if (lastAppliedMappings) {
      setEditedMappings(lastAppliedMappings);
    }
  }, [lastAppliedMappings]);

  // API call handlers
  const handlePreprocessing = useCallback(
    async (instructions?: string) => {
      if (!originalData.length || !selectedFiles.length) {
        const errorMsg =
          'Please upload and process files before starting preprocessing.';
        setValidationErrors([errorMsg]);
        return;
      }

      const startTime = new Date();

      // Set loading state immediately to prevent "All data filtered out" flash
      setIsLoadingTransformedData(true);

      try {
        console.log('🚀 STARTING PREPROCESSING:');
        console.log('Original data length:', originalData.length);
        console.log(
          'Selected files:',
          selectedFiles.map((f) => f.name),
        );

        const csvData = Papa.unparse(originalData);
        console.log('CSV data length:', csvData.length);

        console.log('📡 MAKING API CALL...');
        const response = await preprocessWorkflow({
          data: csvData,
          instructions:
            instructions || 'Clean and prepare the data for transformation.',
          fileNames: selectedFiles.map((f) => f.name),
          sheetName: selectedSheet || '',
          model: selectedModel,
          testMode: useTestMode,
        });

        console.log('✅ API CALL COMPLETED');
        console.log('🔍 PREPROCESSING RESPONSE DEBUG:');
        console.log('Full response:', response);
        console.log('Response data:', response.data);
        console.log(
          'Response data success:',
          response.data.success,
          typeof response.data.success,
        );
        console.log('Response data data:', response.data.data);
        console.log(
          'Response data cleanedCsv length:',
          response.data.data?.cleanedCsv?.length,
        );

        // Check if workflow was successful
        if (!response.data.success) {
          setValidationErrors([
            response.data.error || 'Preprocessing workflow failed',
          ]);
          setPreprocessingWorkflowError(
            response.data.error || 'Preprocessing workflow failed',
          );
          setPreprocessingFailedStepId(response.data.failedStepId);

          const duration = (Date.now() - startTime.getTime()) / 1000;
          aiChatRef.current?.appendAiMessage(
            `❌ Preprocessing failed: ${
              response.data.error || 'Workflow execution failed'
            }`,
            {
              requestStartTime: startTime,
              duration,
            },
          );
          return;
        }

        setPreprocessData(response.data);
        setValidationErrors([]);
        setPreprocessingWorkflowError(undefined);
        setPreprocessingFailedStepId(undefined);

        // Parse and store preprocessed data
        console.log('🔍 CSV PARSING DEBUG:');
        console.log('Raw CSV string:', response.data.data.cleanedCsv);
        console.log('CSV string length:', response.data.data.cleanedCsv.length);

        const parsedCleaned = Papa.parse(response.data.data.cleanedCsv, {
          header: false,
          skipEmptyLines: false,
        });

        console.log('Parsed CSV data:', parsedCleaned.data);
        console.log('Parsed CSV data length:', parsedCleaned.data.length);
        console.log('Parsed CSV errors:', parsedCleaned.errors);

        if (parsedCleaned.errors && parsedCleaned.errors.length > 0) {
          console.error('CSV parsing errors:', parsedCleaned.errors);
        }

        setPreprocessedData(parsedCleaned.data as unknown[][]);
        console.log(
          '✅ Set preprocessedData with length:',
          parsedCleaned.data.length,
        );

        // Clear loading state after data is set
        setIsLoadingTransformedData(false);

        // Add AI response to conversation
        const duration = (Date.now() - startTime.getTime()) / 1000;
        // Use workflowSteps descriptions (user-friendly) instead of explanation array (verbose/technical)
        const stepDescriptions =
          response.data.data.workflowSteps?.map(
            (step: { type: string; description: string }) => step.description,
          ) || [];
        const formattedOps =
          formatWorkflowExplanationsForChat(stepDescriptions);
        const responseMessage = `✅ Preprocessing completed!\n\n${formattedOps}\n\nYour data is ready for the next step.`;

        // Append to AI chat
        aiChatRef.current?.appendAiMessage(responseMessage, {
          requestStartTime: startTime,
          duration,
          model: selectedModel,
        });

        posthog.capture('workflow_preprocessing_completed', {
          context: 'convert_data_page_workflow',
        });
      } catch (error) {
        console.error('❌ PREPROCESSING ERROR:');
        console.error('Error object:', error);
        console.error(
          'Error message:',
          error instanceof Error ? error.message : String(error),
        );
        console.error(
          'Error stack:',
          error instanceof Error ? error.stack : 'No stack trace',
        );

        const errorMessage =
          error instanceof Error ? error.message : 'Preprocessing failed';
        setValidationErrors([errorMessage]);

        const duration = (Date.now() - startTime.getTime()) / 1000;
        aiChatRef.current?.appendAiMessage(
          `❌ Preprocessing failed: ${errorMessage}`,
          {
            requestStartTime: startTime,
            duration,
            model: selectedModel,
          },
        );
      } finally {
        // Always clear the loading state in case of error
        setIsLoadingTransformedData(false);
      }
    },
    [
      originalData,
      selectedFiles,
      selectedSheet,
      selectedModel,
      useTestMode,
      preprocessWorkflow,
    ],
  );

  // Field mapping handler
  const handleFieldMapping = useCallback(
    async (instructions?: string) => {
      if (!preprocessData) {
        setValidationErrors([
          'Please complete preprocessing before field mapping.',
        ]);
        return;
      }

      const startTime = new Date();

      // Set loading state immediately to prevent "All data filtered out" flash
      setIsLoadingMappedData(true);

      try {
        const response = await mapFieldsWorkflow({
          data: preprocessData.data.cleanedCsv,
          instructions:
            instructions || 'Map field values using the provided context.',
          fileNames: selectedFiles.map((f) => f.name),
          sheetName: selectedSheet || '',
          model: selectedModel,
          constants: {
            supplierId: (editedConstants as { supplierId: string }).supplierId,
            currency: (editedConstants as { currency: string }).currency,
          },
          testMode: useTestMode,
        });

        setMappingData(response.data);

        if (response.data.mappings) {
          setEditedMappings(response.data.mappings);
        }

        // Apply field mappings to transform preprocessed data into mapped data
        if (response.data.mappings) {
          try {
            // Use new API for initial mapping application
            const csvData = Papa.unparse(preprocessedData);
            const mappingResponse = await applyMappingsWorkflow({
              data: csvData,
              mappings: response.data.mappings,
            });

            const parsedMapped = Papa.parse(
              mappingResponse.data.transformedCSV,
              {
                skipEmptyLines: true,
              },
            );
            setMappedData(parsedMapped.data as unknown[][]);

            // Clear loading state after data is set
            setIsLoadingMappedData(false);
          } catch (error) {
            console.error(
              'Failed to apply initial mappings, using fallback:',
              error,
            );
            // Fallback to local transformation if API fails
            const transformedData = applyFieldMappings(
              preprocessedData,
              response.data.mappings,
            );
            setMappedData(transformedData);

            // Clear loading state after fallback data is set
            setIsLoadingMappedData(false);
          }
        } else {
          // Fallback if no mappings returned
          setMappedData(preprocessedData);

          // Clear loading state after fallback data is set
          setIsLoadingMappedData(false);
        }

        // Store the last applied mappings after AI extraction and application
        if (response.data.mappings) {
          setLastAppliedMappings(response.data.mappings);
        }

        setValidationErrors([]);

        // Add AI response to conversation
        const duration = (Date.now() - startTime.getTime()) / 1000;
        const responseMessage = `✅ Field mapping completed!\n\nExtracted mapped field values. Review and edit the results before proceeding to workflow generation.`;

        aiChatRef.current?.appendAiMessage(responseMessage, {
          requestStartTime: startTime,
          duration,
          model: selectedModel,
        });

        posthog.capture('workflow_field_mapping_completed', {
          context: 'convert_data_page_workflow',
        });
      } catch (error) {
        console.error('Field mapping error:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Field mapping failed';
        setValidationErrors([errorMessage]);

        const duration = (Date.now() - startTime.getTime()) / 1000;
        aiChatRef.current?.appendAiMessage(
          `❌ Field mapping failed: ${errorMessage}`,
          {
            requestStartTime: startTime,
            duration,
            model: selectedModel,
          },
        );
      } finally {
        // Always clear the loading state in case of error
        setIsLoadingMappedData(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      preprocessData,
      preprocessedData,
      selectedFiles,
      selectedSheet,
      selectedModel,
      mapFieldsWorkflow,
      applyMappingsWorkflow,
    ],
  );

  // Workflow generation handler
  const handleWorkflowGeneration = useCallback(
    async (instructions?: string) => {
      if (!mappedData.length) {
        setValidationErrors([
          'Please complete preprocessing and field mapping before workflow generation.',
        ]);
        return;
      }

      // Validate required constants
      if (!editedConstants?.supplierId || !editedConstants?.currency) {
        setValidationErrors([
          'Required constants are missing. Please ensure supplier ID and currency are set in the mapping phase.',
        ]);
        return;
      }

      const startTime = new Date();

      // Set loading state immediately to prevent "All data filtered out" flash
      setIsLoadingFinalData(true);

      try {
        // Convert mapped data to CSV for backend
        const mappedCsv = Papa.unparse(mappedData);

        const payload = {
          mappedCsv,
          instructions:
            instructions || 'Generate and execute the transformation workflow.',
          fileNames: selectedFiles.map((f) => f.name),
          sheetName: selectedSheet || '',
          model: selectedModel,
          constants: editedConstants as WorkflowConstants,
          testMode: useTestMode,
        };

        const response = await generateWorkflow(payload);

        // Always set workflow data so UI can display steps (even on failure)
        setWorkflowData(response.data);

        // Check if workflow was successful
        if (!response.data.success) {
          setValidationErrors([
            response.data.error || 'Workflow generation failed',
          ]);
          setWorkflowGenerationError(
            response.data.error || 'Workflow generation failed',
          );
          setWorkflowGenerationFailedStepId(response.data.failedStepId);

          const duration = (Date.now() - startTime.getTime()) / 1000;
          aiChatRef.current?.appendAiMessage(
            `❌ Workflow generation failed: ${
              response.data.error || 'Workflow execution failed'
            }`,
            {
              requestStartTime: startTime,
              duration,
            },
          );
          return;
        }
        setValidationErrors([]);
        setWorkflowGenerationError(undefined);
        setWorkflowGenerationFailedStepId(undefined);

        // Parse and display final transformed CSV
        const parsedFinal = Papa.parse(response.data.data.transformedCsv, {
          skipEmptyLines: true,
        });
        setTransformedData(parsedFinal.data as unknown[][]);

        // Store pre-conversion data for currency conversion
        setPreConversionData(parsedFinal.data as unknown[][]);
        setConversionApplied(false); // Reset conversion applied flag on new workflow
        setAppliedConversionRate(undefined); // Reset applied rate on new workflow

        // Auto-apply currency conversion if a rate was previously set
        const previousConversionRate = editedConstants.conversionRate;
        if (previousConversionRate && previousConversionRate > 0) {
          const sourceCurrency = editedConstants.currency || 'USD';
          const targetCurrency = editedConstants.targetCurrency || 'CAD';

          console.log('🔄 Auto-applying currency conversion:', {
            rate: previousConversionRate,
            sourceCurrency,
            targetCurrency,
            workflowDataRows: parsedFinal.data.length,
            workflowHeaders: parsedFinal.data[0],
          });

          const convertedData = applyCurrencyConversion(
            parsedFinal.data as unknown[][],
            previousConversionRate,
            sourceCurrency,
            REBATE_CSV_COLUMNS.VALUE,
            targetCurrency,
          );

          console.log('✅ Auto-apply complete:', {
            convertedDataRows: convertedData.length,
            convertedHeaders: convertedData[0],
          });

          setTransformedData(convertedData);
          setConversionApplied(true);
          setAppliedConversionRate(previousConversionRate); // Track as applied

          console.log(
            `Auto-applied currency conversion: ${previousConversionRate} ${sourceCurrency} → ${targetCurrency}`,
          );
        }

        // Snapshot the constants that were used for this workflow run
        setAppliedWorkflowConstants({ ...editedConstants });

        // Clear loading state after data is set
        setIsLoadingFinalData(false);

        setValidationErrors([]);

        // Add AI response to conversation
        const duration = (Date.now() - startTime.getTime()) / 1000;
        // Format workflow step descriptions for chat display
        const stepExplanations =
          response.data.data.workflowSteps?.map(
            (step: { type: string; description: string }) =>
              `${step.type}: ${step.description}`,
          ) || [];
        const formattedSteps =
          formatWorkflowExplanationsForChat(stepExplanations);

        const conversionMessage = previousConversionRate
          ? `\n💱 Currency conversion auto-applied: ${previousConversionRate} ${
              editedConstants.currency
            } → ${editedConstants.targetCurrency || 'CAD'}\n`
          : '';

        const responseMessage = `✅ Workflow completed!\n\n${formattedSteps}\n${conversionMessage}\nYour data transformation is complete and ready for download or submission.`;

        aiChatRef.current?.appendAiMessage(responseMessage, {
          requestStartTime: startTime,
          duration,
          model: selectedModel,
        });

        // Mark phase 3 as completed

        posthog.capture('workflow_generation_completed', {
          context: 'convert_data_page_workflow',
          auto_conversion_applied: Boolean(previousConversionRate),
          conversion_rate: previousConversionRate,
        });
      } catch (error) {
        console.error('Workflow generation error:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Workflow generation failed';
        setValidationErrors([errorMessage]);

        const duration = (Date.now() - startTime.getTime()) / 1000;
        aiChatRef.current?.appendAiMessage(
          `❌ Workflow generation failed: ${errorMessage}`,
          {
            requestStartTime: startTime,
            duration,
            model: selectedModel,
          },
        );
      } finally {
        // Always clear the loading state in case of error
        setIsLoadingFinalData(false);
      }
    },
    [
      mappedData,
      editedConstants,
      selectedFiles,
      selectedSheet,
      selectedModel,
      useTestMode,
      generateWorkflow,
    ],
  );

  // Re-apply field mappings with current edited mappings using new API
  const handleRefreshMappings = useCallback(async () => {
    if (preprocessedData.length === 0 || !editedMappings) {
      return;
    }

    // Capture the mappings at the start to store after successful apply
    const mappingsToApply = editedMappings;

    setIsApplyingMappingsLocal(true);

    try {
      // Convert preprocessed data to CSV for the API
      const csvData = Papa.unparse(preprocessedData);

      const response = await applyMappingsWorkflow({
        data: csvData,
        mappings: mappingsToApply,
      });

      // Parse the transformed CSV back to 2D array
      const parsedTransformed = Papa.parse(response.data.transformedCSV, {
        skipEmptyLines: true,
      });
      setMappedData(parsedTransformed.data as unknown[][]);

      // Store the successfully applied mappings
      setLastAppliedMappings(mappingsToApply);
    } catch (error) {
      console.error('Failed to apply mappings:', error);
      // Fallback to local transformation if API fails
      const transformedData = applyFieldMappings(
        preprocessedData,
        mappingsToApply,
      );
      setMappedData(transformedData);

      // Store the successfully applied mappings (even in fallback)
      setLastAppliedMappings(mappingsToApply);
    } finally {
      setIsApplyingMappingsLocal(false);
    }
  }, [preprocessedData, editedMappings, applyMappingsWorkflow]);

  // Check if there are unapplied changes to constants (currency or date range)
  const hasUnappliedConstantChanges = useMemo(() => {
    if (!appliedWorkflowConstants || !editedConstants) return false;

    // Check if currency changed
    if (appliedWorkflowConstants.currency !== editedConstants.currency) {
      return true;
    }

    // Check if date range changed
    const appliedStart = appliedWorkflowConstants.dateRange?.startDate;
    const appliedEnd = appliedWorkflowConstants.dateRange?.endDate;
    const editedStart = editedConstants.dateRange?.startDate;
    const editedEnd = editedConstants.dateRange?.endDate;

    if (appliedStart !== editedStart || appliedEnd !== editedEnd) {
      return true;
    }

    return false;
  }, [appliedWorkflowConstants, editedConstants]);

  // Check if conversion rate changed but not applied
  // Compare current rate in constants with last applied rate (like PhaseTwoMapping)
  const hasUnappliedConversionRateChange = useMemo(() => {
    // If no conversion has been applied yet, no unapplied change
    if (!conversionApplied) return false;

    // Compare current rate with last applied rate
    const currentRate = editedConstants?.conversionRate;

    return currentRate !== appliedConversionRate;
  }, [editedConstants, conversionApplied, appliedConversionRate]);

  // Reset constants to last applied workflow constants
  const handleResetConstants = useCallback(() => {
    if (appliedWorkflowConstants) {
      // Reset to last applied constants (which includes the last applied rate)
      setEditedConstants({ ...appliedWorkflowConstants });

      // If conversion was applied, revert to pre-conversion data
      if (conversionApplied) {
        setTransformedData([...preConversionData]);
        setConversionApplied(false);
        setAppliedConversionRate(undefined);
      }
    }
  }, [appliedWorkflowConstants, conversionApplied, preConversionData]);

  // Handle rate change in input (updates constants immediately)
  const handleConversionRateChange = useCallback((rate: number | undefined) => {
    setEditedConstants((prev) => ({
      ...(prev as WorkflowConstants),
      conversionRate: rate,
      targetCurrency: prev?.targetCurrency || 'CAD',
    }));
  }, []);

  // Currency conversion handler (applies conversion to data)
  const handleApplyCurrencyConversion = useCallback(
    (rate: number) => {
      if (!preConversionData.length) {
        console.error('No pre-conversion data available');
        return;
      }

      const sourceCurrency = editedConstants?.currency || 'USD';
      const targetCurrency = editedConstants?.targetCurrency || 'CAD';

      console.log('🔄 Applying currency conversion:', {
        rate,
        sourceCurrency,
        targetCurrency,
        preConversionDataRows: preConversionData.length,
        preConversionHeaders: preConversionData[0],
      });

      // Apply conversion to the pre-conversion data (not current transformedData)
      // This allows users to change the rate without re-running the workflow
      const convertedData = applyCurrencyConversion(
        preConversionData,
        rate,
        sourceCurrency,
        REBATE_CSV_COLUMNS.VALUE,
        targetCurrency,
      );

      console.log('✅ Currency conversion applied:', {
        convertedDataRows: convertedData.length,
        convertedHeaders: convertedData[0],
      });

      setTransformedData(convertedData);
      setConversionApplied(true);

      // Track that this rate has been applied (for unapplied changes detection)
      setAppliedConversionRate(rate);

      // Update appliedWorkflowConstants to reflect the newly applied conversion rate
      // This ensures the "unapplied changes" check doesn't trigger after applying
      if (editedConstants) {
        setAppliedWorkflowConstants({
          ...editedConstants,
          conversionRate: rate,
        });
      }

      posthog.capture('workflow_currency_conversion_applied', {
        context: 'convert_data_page_workflow',
        source_currency: sourceCurrency,
        target_currency: targetCurrency,
        conversion_rate: rate,
      });
    },
    [preConversionData, editedConstants],
  );

  // Simple phase management with auto-run and backwards state clearing
  const changePhase = useCallback(
    async (phase: WorkflowPhase) => {
      // Prevent phase changes during API operations
      if (
        isPreprocessing ||
        isMapping ||
        isApplyingMappings ||
        isGeneratingWorkflow
      ) {
        return;
      }

      const isGoingBackwards = phase < currentPhase;

      // Clear downstream state when going backwards to prevent stale data
      if (isGoingBackwards) {
        // If going back to phase 0 or 1, clear everything from phase 2 onwards
        if (phase <= 1) {
          setMappingData(null);
          setEditedConstants(null);
          setEditedMappings({
            divisionMappings: {
              sourceColumnNames: [],
              targetColumnName: '',
              mappings: {},
              separator: '|||',
            },
            productMappings: {
              sourceColumnNames: [],
              targetColumnName: '',
              mappings: {},
              separator: '|||',
            },
            dateMappings: {
              sourceColumnNames: [],
              targetColumnName: '',
              mappings: {},
              separator: '|||',
            },
          });
          setMappedData([]);
          setLastAppliedMappings(null); // Clear last applied mappings
        }

        // If going back to phase 0, 1, or 2, clear phase 3 state
        if (phase <= 2) {
          setWorkflowData(null);
          setTransformedData([]);
        }

        // If going back to phase 0, clear phase 1 state too
        if (phase <= 0) {
          setPreprocessData(null);
          setPreprocessedData([]);
        }

        // Clear any errors when going backwards
        setTransformError(null);
        setValidationErrors([]);

        // Clear any in-progress loading states
        setIsLoadingTransformedData(false);
        setIsLoadingMappedData(false);
        setIsLoadingFinalData(false);

        // Track backwards navigation for analytics
        posthog.capture('workflow_phase_backward_navigation', {
          from_phase: currentPhase,
          to_phase: phase,
          cleared_states:
            phase <= 1
              ? ['mapping', 'workflow']
              : phase <= 2
                ? ['workflow']
                : [],
        });
      }

      setCurrentPhase(phase);

      // Auto-run logic when entering each phase (only when going forwards)
      if (!isGoingBackwards) {
        switch (phase) {
          case 0:
            // Image parsing will be handled by useEffect
            break;
          case 1:
            if (
              originalData.length > 0 &&
              !preprocessData &&
              !isPreprocessing
            ) {
              aiChatRef.current?.appendAiMessage(
                '🔄 Auto-running Phase 1: Preprocessing\n\nCleaning and preparing your data for transformation...',
                { model: selectedModel },
              );
              await handlePreprocessing();
            }
            break;
          case 2:
            if (preprocessData && !mappingData && !isMapping) {
              aiChatRef.current?.appendAiMessage(
                '🔄 Auto-running Phase 2: Field Mapping\n\nExtracting constants and mapping field values...',
                { model: selectedModel },
              );
              await handleFieldMapping();
            }
            break;
          case 3:
            // Before transitioning to phase 3, ensure mappings are applied to get latest mappedData
            // This ensures any user changes in the mapping UI are applied before workflow generation
            if (preprocessedData.length > 0 && editedMappings) {
              await handleRefreshMappings();
            }

            if (
              preprocessData &&
              mappingData &&
              !workflowData &&
              !isGeneratingWorkflow
            ) {
              aiChatRef.current?.appendAiMessage(
                '🔄 Auto-running Phase 3: Workflow Generation\n\nGenerating and executing the transformation workflow with your mappings and constants...',
                { model: selectedModel },
              );
              await handleWorkflowGeneration();
            }
            break;
        }
      }
    },
    [
      currentPhase,
      originalData,
      preprocessData,
      mappingData,
      workflowData,
      isPreprocessing,
      isMapping,
      isApplyingMappings,
      isGeneratingWorkflow,
      handlePreprocessing,
      handleFieldMapping,
      handleWorkflowGeneration,
      handleRefreshMappings,
      preprocessedData,
      editedMappings,
      selectedModel,
    ],
  );

  // File processing functions
  const processCSVData = useCallback(
    (text: string, skipFileTypeSet = false) => {
      // Set file type for CSV processing (unless this is being called from image processing)
      if (!skipFileTypeSet) {
        setFileType('csv');
      }

      const parsed = Papa.parse(text, { skipEmptyLines: true });
      const rawData = parsed.data as unknown[][];

      // Process CSV data using unified utility function
      const hierarchicalOptions: Partial<HierarchicalParseOptions> = {
        enableHierarchicalGrouping: true,
        parentColumnSuffix: '',
        childColumnSuffix: ' sub col a',
      };

      const { processedData, hierarchyReport } = processSheetData(
        rawData,
        hierarchicalOptions,
      );

      setOriginalData(processedData);
      setTransformedData(
        sortTransformedData(processedData.map((row) => [...row])),
      );
      setIsLoadingOriginal(false);
    },
    [],
  );

  const loadSheetData = useCallback(
    async (workbook: XLSX.WorkBook, sheetName: string) => {
      setSelectedSheet(sheetName);
      setIsLoadingOriginal(true);
      setTransformError(null);

      try {
        const sheet = workbook.Sheets[sheetName];

        // Process sheet using unified utility function
        const hierarchicalOptions: Partial<HierarchicalParseOptions> = {
          enableHierarchicalGrouping: true,
          parentColumnSuffix: '',
          childColumnSuffix: ' sub col a',
        };

        const { processedData, hierarchyReport } = processExcelSheet(sheet, {
          hierarchicalOptions,
          sheetName,
        });

        // Generate and display AI message if hierarchical transformation was applied
        if (hierarchyReport?.applied) {
          const message = generateHierarchicalMessage(hierarchyReport);
          if (message) {
            aiChatRef.current?.appendAiMessage(message);
          }
        }

        setOriginalData(processedData);
        setTransformedData([]);
        setIsLoadingOriginal(false);
      } catch (err) {
        setTransformError(
          'Failed to process data. Please try a different sheet or contact support.',
        );
        setTransformedData([]);
        setIsLoadingOriginal(false);
      }
    },
    [],
  );

  const processXLSXData = useCallback(
    async (data: string): Promise<XLSX.WorkBook> => {
      // Set file type for XLSX processing
      setFileType('xlsx');

      const newWorkbook = XLSX.read(data, { type: 'binary' });
      setWorkbook(newWorkbook);

      if (newWorkbook.SheetNames.length > 0) {
        await loadSheetData(newWorkbook, newWorkbook.SheetNames[0]);
      }

      return newWorkbook;
    },
    [loadSheetData],
  );

  // Main file processing logic
  const processFileWithNavigation = useCallback(
    async (fileType: 'csv' | 'xlsx' | 'image', data: string, files: File[]) => {
      setIsLoadingFileProcessing(true);
      setFileProcessingError(null);
      setValidationErrors([]);
      setSelectedFiles(files);

      try {
        switch (fileType) {
          case 'csv': {
            processCSVData(data);
            await changePhase(0);
            break;
          }

          case 'xlsx': {
            const newWorkbook = await processXLSXData(data);

            if (newWorkbook) {
              const sheetCount = newWorkbook.SheetNames.length;

              if (sheetCount === 1) {
                // Single sheet - go to Phase 0 (constants)
                const singleSheet = newWorkbook.SheetNames[0];
                setSelectedSheet(singleSheet);
                await loadSheetData(newWorkbook, singleSheet);
                await changePhase(0);
              } else {
                // Multiple sheets - go to Phase 0 for selection
                const defaultSheet = newWorkbook.SheetNames[0];
                setSelectedSheet(defaultSheet);
                await loadSheetData(newWorkbook, defaultSheet);
                await changePhase(0);
              }
            } else {
              throw new Error('Failed to load Excel workbook');
            }
            break;
          }

          case 'image': {
            // Set file type and image review mode (API call will happen in Phase 0)
            setFileType('image');
            setIsImageReviewMode(true);

            // Go to Phase 0 for image review (useEffect will trigger the API call)
            await changePhase(0);
            break;
          }
        }
      } catch (error) {
        console.error('File processing error:', error);
        setFileProcessingError(
          error instanceof Error ? error.message : 'File processing failed',
        );
        setValidationErrors([
          error instanceof Error ? error.message : 'File processing failed',
        ]);
      } finally {
        setIsLoadingFileProcessing(false);
      }
    },
    [processCSVData, processXLSXData, loadSheetData, changePhase],
  );

  // Dedicated function for image parsing API call (used by both auto-trigger and re-parsing)
  const parseImagesWithCustomInstructions = useCallback(
    async (customInstructions?: string) => {
      if (!selectedFiles.length || fileType !== 'image') {
        console.error('No image files to parse');
        return;
      }

      try {
        const response = await parseCsvFromImage({
          files: selectedFiles,
          context:
            'Data transformation workflow - convert uploaded image(s) to structured CSV data for processing.',
          customInstructions:
            customInstructions ||
            'Extract all tabular data from the image(s) and convert to CSV format with proper headers and data types.',
          model: selectedModel,
        });

        if (response?.data?.csvData) {
          // Process the parsed CSV data (skip setting fileType since this is image processing)
          processCSVData(response.data.csvData, true);

          // If OCR returned constants, initialize edited constants (fill in strategy default)
          const respConstants = response.data.constants as
            | Record<string, unknown>
            | undefined;
          if (respConstants) {
            // @ts-expect-error FIXME this could error due to WorkflowConstants type mismatch
            setEditedConstants((prev) => ({ ...prev, ...respConstants }));
          }

          // Mark phase 0 as completed for images (data is parsed and ready)

          posthog.capture('workflow_image_parse_completed', {
            context: 'convert_data_page_workflow',
            custom_instructions_provided: Boolean(customInstructions),
            auto_triggered: !customInstructions,
          });
        } else {
          throw new Error(
            'Failed to parse image data - no CSV data returned from API',
          );
        }
      } catch (error) {
        console.error('Image parsing error:', error);
        setFileProcessingError(
          error instanceof Error ? error.message : 'Image parsing failed',
        );
        setValidationErrors([
          error instanceof Error ? error.message : 'Image parsing failed',
        ]);
      }
    },
    [selectedFiles, fileType, parseCsvFromImage, selectedModel, processCSVData],
  );

  // Helper to run constants extraction for CSV/XLSX in a single endpoint call
  const detectConstantsFromCurrentData = useCallback(
    async (customInstructions?: string) => {
      if (!originalData.length) return;
      setIsDetectingConstants(true);
      try {
        const rawCsv = Papa.unparse(originalData);
        const baseInstructions =
          'Extract supplier ID (not name), currency, and overall date range (ISO). If dates lack a year, infer from filename/headers; fallback to current year if missing. Return ISO YYYY-MM-DD for start/end.';
        const mergedInstructions = customInstructions
          ? `${baseInstructions}\n\nUser instructions: ${customInstructions}`
          : baseInstructions;
        const constantsResp = await extractConstants({
          data: rawCsv,
          instructions: mergedInstructions,
          fileNames: selectedFiles.map((f) => f.name),
          sheetName: selectedSheet || '',
          model: selectedModel,
          testMode: useTestMode,
        });
        type ExtractedConstantsRaw = {
          supplierId?: string;
          supplierName?: string;
          // NOTE: currency is NO LONGER returned by backend - it's fetched from Company.metadata.defaultCurrency
          dateRange?: { startDate: string; endDate: string };
        };

        const nextRaw = (constantsResp.data.constants ||
          {}) as ExtractedConstantsRaw;
        // Normalize supplierName -> supplierId by looking up the supplier if needed
        let normalized: Partial<WorkflowConstants> = {};
        if (nextRaw.supplierId) {
          normalized = {
            supplierId: nextRaw.supplierId,
            // NOTE: currency is NO LONGER extracted by AI - will be fetched reactively via useEffect
            dateRange: nextRaw.dateRange,
          };
        } else if (nextRaw.supplierName) {
          const name = String(nextRaw.supplierName || '').trim();
          normalized = {
            // NOTE: currency is NO LONGER extracted by AI - will be fetched reactively via useEffect
            dateRange: nextRaw.dateRange,
          };
          // Try to resolve name to id via search API
          try {
            const res = await fetch(
              `${
                config.apiBaseUrl
              }/accounts-payable/suppliers/search?query=${encodeURIComponent(
                name,
              )}&limit=1`,
              { credentials: 'include' },
            );
            if (res.ok) {
              const json = (await res.json()) as {
                data?: { suppliers?: Array<{ id: string; name: string }> };
                suppliers?: Array<{ id: string; name: string }>;
              };
              const suppliers = json.data?.suppliers || json.suppliers || [];
              const first = suppliers[0];
              if (first?.id) {
                normalized = { ...normalized, supplierId: first.id };
              }
            }
          } catch {
            // ignore
          }
        }

        setEditedConstants(
          (prev) => ({ ...(prev ?? {}), ...normalized }) as WorkflowConstants,
        );
      } catch (e) {
        console.error('Detect constants failed', e);
      } finally {
        setIsDetectingConstants(false);
      }
    },
    [
      originalData,
      selectedFiles,
      selectedSheet,
      selectedModel,
      useTestMode,
      extractConstants,
    ],
  );

  // Auto-trigger constants extraction in Phase 0 for CSV/XLSX
  useEffect(() => {
    const isMultiSheetXlsx = Boolean(
      workbook &&
        Array.isArray(workbook.SheetNames) &&
        workbook.SheetNames.length > 1,
    );
    const shouldAutoDetect =
      currentPhase === 0 &&
      !isImageReviewMode &&
      originalData.length > 0 &&
      !isDetectingConstants &&
      !isExtractingConstants &&
      !constantsAutoTriggeredRef.current &&
      !isMultiSheetXlsx; // Do not auto-run for multi-sheet XLSX

    if (shouldAutoDetect) {
      constantsAutoTriggeredRef.current = true;
      void detectConstantsFromCurrentData();
    }
  }, [
    currentPhase,
    isImageReviewMode,
    originalData.length,
    isDetectingConstants,
    isExtractingConstants,
    detectConstantsFromCurrentData,
    workbook,
  ]);

  // Auto-trigger moved to PhaseZeroSheetSelection component

  // Image re-parsing handler for Phase 0 (uses the shared function)
  const handleImageReparse = useCallback(
    async (customInstructions: string | undefined) => {
      if (isImageReviewMode || fileType === 'image') {
        await parseImagesWithCustomInstructions(customInstructions);
      } else {
        await detectConstantsFromCurrentData(customInstructions);
      }
    },
    [
      parseImagesWithCustomInstructions,
      detectConstantsFromCurrentData,
      isImageReviewMode,
      fileType,
    ],
  );

  // File handling - just store files, don't process immediately
  const handleFileUpload = useCallback((files: File[]) => {
    if (files.length === 0) return;

    setSelectedFiles(files);

    // Clear any previous errors
    setFileProcessingError(null);
    setValidationErrors([]);
  }, []);

  // Process files when user clicks "Process" button
  const handleProcessFiles = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    // Get file extensions for validation
    const fileExtensions = selectedFiles.map(
      (file) => file.name.split('.').pop()?.toLowerCase() || '',
    );

    const imageExtensions = ['png', 'jpg', 'jpeg', 'pdf', 'bmp', 'tiff', 'gif'];
    const csvExtensions = ['csv'];
    const xlsxExtensions = ['xlsx', 'xls'];

    const hasImages = fileExtensions.some((ext) =>
      imageExtensions.includes(ext),
    );
    const hasCsv = fileExtensions.some((ext) => csvExtensions.includes(ext));
    const hasXlsx = fileExtensions.some((ext) => xlsxExtensions.includes(ext));

    // Validation: Check for mixed file types
    const fileTypeCount = [hasImages, hasCsv, hasXlsx].filter(Boolean).length;
    if (fileTypeCount > 1) {
      const errorMessage =
        'Please upload only one type of file at a time (either images, CSV, or XLSX files).';
      setFileProcessingError(errorMessage);
      return;
    }

    // Validation: Check file limits
    if (hasCsv && selectedFiles.length > 1) {
      const errorMessage = 'Only one CSV file can be processed at a time.';
      setFileProcessingError(errorMessage);
      return;
    }

    if (hasXlsx && selectedFiles.length > 1) {
      const errorMessage = 'Only one XLSX file can be processed at a time.';
      setFileProcessingError(errorMessage);
      return;
    }

    // Validation: Check for unsupported file types
    const supportedExtensions = [
      ...imageExtensions,
      ...csvExtensions,
      ...xlsxExtensions,
    ];
    const unsupportedFiles = fileExtensions.filter(
      (ext) => !supportedExtensions.includes(ext),
    );
    if (unsupportedFiles.length > 0) {
      const errorMessage = `Unsupported file types: ${unsupportedFiles.join(
        ', ',
      )}. Please upload CSV, XLSX, or image files (PNG, JPG, JPEG, PDF, BMP, TIFF, GIF).`;
      setFileProcessingError(errorMessage);
      return;
    }

    // Clear any previous errors since validation passed
    setFileProcessingError(null);

    if (hasImages) {
      setFileType('image');
      setIsImageReviewMode(true);

      await changePhase(0);

      return;
    }

    // Handle CSV/XLSX files with FileReader (single file only)
    const file = selectedFiles[0];
    const extension = fileExtensions[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      const result = e.target?.result;
      if (!result) return;

      if (extension === 'csv') {
        await processFileWithNavigation('csv', result as string, selectedFiles);
      } else if (extension === 'xlsx' || extension === 'xls') {
        await processFileWithNavigation(
          'xlsx',
          result as string,
          selectedFiles,
        );
      }
    };

    if (extension === 'csv') {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  }, [selectedFiles, processFileWithNavigation, changePhase]);

  // UI event handlers
  const handleSheetChange = useCallback(
    async (sheetName: string) => {
      if (workbook && sheetName !== selectedSheet) {
        setSelectedSheet(sheetName);
        await loadSheetData(workbook, sheetName);
      }
    },
    [workbook, selectedSheet, loadSheetData],
  );

  // Additional handlers for UI components
  const handleContinue = useCallback(async () => {
    const nextPhaseNumber = currentPhase + 1;
    if (nextPhaseNumber > 3) return;

    // When transitioning from Phase 2 to Phase 3, check for aliases first
    if (currentPhase === 2 && phaseTwoMappingRef.current) {
      phaseTwoMappingRef.current.checkAliasesBeforeContinue(() => {
        void changePhase(nextPhaseNumber as WorkflowPhase);
      });
      return;
    }

    await changePhase(nextPhaseNumber as WorkflowPhase);
  }, [currentPhase, changePhase]);

  const handleBack = useCallback(async () => {
    const prevPhase = currentPhase - 1;
    if (prevPhase >= 0) {
      await changePhase(prevPhase as WorkflowPhase);
    }
  }, [currentPhase, changePhase]);

  /**
   * Extract date range from transformed data for overlap checking
   */
  const extractDateRange = useCallback(
    (data: unknown[][]): { startDate: string; endDate: string } | null => {
      if (data.length < 2) return null; // Need header + at least 1 row

      const headers = data[0] as string[];
      const dateColumnIndex = headers.findIndex(
        (h) => h === REBATE_CSV_COLUMNS.DATE,
      );

      if (dateColumnIndex === -1) return null;

      const dates: Date[] = [];
      for (let i = 1; i < data.length; i++) {
        const row = data[i] as unknown[];
        const dateStr = row[dateColumnIndex] as string;
        if (dateStr) {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            dates.push(date);
          }
        }
      }

      if (dates.length === 0) return null;

      const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
      const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

      return {
        startDate: minDate.toISOString().split('T')[0],
        endDate: maxDate.toISOString().split('T')[0],
      };
    },
    [],
  );

  /**
   * Perform the actual submission with optional overlap resolution
   */
  const performSubmission = useCallback(
    async (overlapResolution?: OverlapResolution) => {
      const pending = pendingSubmissionRef.current;
      if (!pending || !editedConstants) {
        console.error('No pending submission data');
        return;
      }

      try {
        const response = await submitTransformedCSV({
          transformationType: 'rebate-invoices',
          constants: editedConstants,
          transformedData: pending.csvContent,
          filename: pending.fileName,
          sheetName: selectedSheet || undefined,
          originalFiles: selectedFiles,
          overlapResolution,
        });

        // Clear pending submission
        pendingSubmissionRef.current = null;

        // Show success message
        posthog.capture('convert_data_page_workflow_submit_completed', {
          context: 'convert_data_page_workflow',
          agreement_id: response.data.agreementId,
          overlap_resolution: overlapResolution || 'none',
        });

        showToast({
          title: 'Workflow Complete!',
          message: `Successfully submitted ${response.data.totalLineItems} line items.`,
          color: 'success',
          icon: 'celebration',
        });

        // If this was from a pending upload, mark it as completed
        if (pendingUploadId) {
          try {
            await updatePendingUpload({
              id: pendingUploadId,
              status: 'completed',
              resultAgreementId: response.data.agreementId,
            });
          } catch (e) {
            console.error('Failed to update pending upload status:', e);
          }
        }

        navigate({
          to: '/rebates/agreements/$id',
          params: { id: response.data.agreementId },
        });
      } catch (error: unknown) {
        console.error('Submit error:', error);

        const errorData = error as {
          response?: { data?: { details?: unknown; error?: string } };
          message?: string;
        };

        let errorMessage = 'An unexpected error occurred during submission.';

        if (errorData?.response?.data?.details) {
          const details = errorData.response.data.details;
          console.error('Validation errors:', details);
          const errorMessages = Array.isArray(details)
            ? details.map(String)
            : Object.values(details as Record<string, unknown>)
                .flat()
                .map(String);
          errorMessage = errorMessages.join('; ');
          setValidationErrors(errorMessages);
        } else if (errorData?.response?.data?.error) {
          errorMessage = errorData.response.data.error;
          setValidationErrors([errorMessage]);
        } else if (errorData?.message?.includes('No original files uploaded')) {
          errorMessage =
            'No original files uploaded. Please include the original files that were transformed into this CSV.';
          setValidationErrors([errorMessage]);
        } else {
          errorMessage = errorData?.message || errorMessage;
          setValidationErrors([errorMessage]);
        }

        showToast({
          title: 'Submission Failed',
          message: errorMessage,
          color: 'error',
          icon: 'error',
          persistent: true,
        });
      } finally {
        setConfirmSubmitOpen(false);
        setOverlapModalOpen(false);
      }
    },
    [
      editedConstants,
      selectedSheet,
      selectedFiles,
      submitTransformedCSV,
      showToast,
      navigate,
      pendingUploadId,
      updatePendingUpload,
    ],
  );

  /**
   * Handle overlap resolution choice from modal
   */
  const handleOverlapResolve = useCallback(
    async (resolution: OverlapResolution) => {
      await performSubmission(resolution);
    },
    [performSubmission],
  );

  // Submit handler for Phase 3
  const handleSubmit = useCallback(async () => {
    if (
      !selectedFiles.length ||
      !transformedData.length ||
      Object.keys(editedConstants ?? {}).length === 0
    ) {
      setValidationErrors([
        'Please ensure files are selected, data is transformed, and constants are extracted before submitting.',
      ]);
      return;
    }

    posthog.capture('convert_data_page_workflow_submit_button_clicked', {
      context: 'convert_data_page_workflow',
    });

    try {
      const firstFile = selectedFiles[0];
      const fileName = firstFile.name;

      // Generate CSV string from the transformed data
      const csvContent = generateCsvString(transformedData);

      console.log(
        'CSV content preview (first 500 chars):',
        csvContent.substring(0, 500),
      );

      // Store pending submission data for potential retry after overlap resolution
      pendingSubmissionRef.current = { csvContent, fileName };

      // Check for overlapping data before submission
      const supplierId = editedConstants?.supplierId;
      const dateRange = extractDateRange(transformedData);

      if (supplierId && dateRange) {
        console.log(
          `Checking for overlaps: supplier=${supplierId}, range=${dateRange.startDate} to ${dateRange.endDate}`,
        );

        const overlapResult = await checkOverlap({
          supplierId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        });

        if (overlapResult.data.hasOverlap) {
          console.log(
            `Found ${overlapResult.data.overlappingInvoices.length} overlapping invoices`,
          );

          // Store overlap data and show modal
          setOverlappingInvoices(overlapResult.data.overlappingInvoices);
          setTotalAffectedLineItems(overlapResult.data.totalAffectedLineItems);
          setOverlapModalOpen(true);
          setConfirmSubmitOpen(false);
          return; // Wait for user to resolve overlap
        }
      }

      // No overlap - proceed with submission
      await performSubmission();
    } catch (error: unknown) {
      console.error('Submit error:', error);

      const errorData = error as {
        response?: { data?: { details?: unknown; error?: string } };
        message?: string;
      };

      let errorMessage = 'An unexpected error occurred during submission.';

      if (errorData?.response?.data?.details) {
        const details = errorData.response.data.details;
        console.error('Validation errors:', details);
        const errorMessages = Array.isArray(details)
          ? details.map(String)
          : Object.values(details as Record<string, unknown>)
              .flat()
              .map(String);
        errorMessage = errorMessages.join('; ');
        setValidationErrors(errorMessages);
      } else if (errorData?.response?.data?.error) {
        errorMessage = errorData.response.data.error;
        setValidationErrors([errorMessage]);
      } else if (errorData?.message?.includes('No original files uploaded')) {
        errorMessage =
          'No original files uploaded. Please include the original files that were transformed into this CSV.';
        setValidationErrors([errorMessage]);
      } else {
        errorMessage = errorData?.message || errorMessage;
        setValidationErrors([errorMessage]);
      }

      showToast({
        title: 'Submission Failed',
        message: errorMessage,
        color: 'error',
        icon: 'error',
        persistent: true,
      });
    } finally {
      setConfirmSubmitOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFiles,
    transformedData,
    editedConstants,
    selectedSheet,
    checkOverlap,
    extractDateRange,
    performSubmission,
    showToast,
  ]);

  // Show submit confirmation dialog
  const handleSubmitClick = useCallback(() => {
    setConfirmSubmitOpen(true);
  }, []);

  // View mode change handler removed - now managed internally by SpreadsheetViewers

  const clearErrors = useCallback(() => {
    setFileProcessingError(null);
    setTransformError(null);
    setValidationErrors([]);
  }, []);

  const handleClearConversation = useCallback(() => {
    aiChatRef.current?.clearConversation();
  }, []);

  // Reset all workflow state and return to file upload
  const resetWorkflow = useCallback(() => {
    // Reset phase management
    setCurrentPhase(-1);

    // Reset file and sheet data
    setSelectedFiles([]);
    setWorkbook(null);
    setSelectedSheet(null);
    setFileType(null);
    setIsImageReviewMode(false);

    // Reset all data arrays
    setOriginalData([]);
    setPreprocessedData([]);
    setMappedData([]);
    setTransformedData([]);

    // Reset phase results
    setPreprocessData(null);
    setMappingData(null);
    setEditedConstants(null);
    setEditedMappings({
      divisionMappings: {
        sourceColumnNames: [],
        targetColumnName: '',
        mappings: {},
        separator: '|||',
      },
      productMappings: {
        sourceColumnNames: [],
        targetColumnName: '',
        mappings: {},
        separator: '|||',
      },
      dateMappings: {
        sourceColumnNames: [],
        targetColumnName: '',
        mappings: {},
        separator: '|||',
      },
    });
    setWorkflowData(null);
    setLastAppliedMappings(null);

    // Chat component maintains its own conversation state

    // Reset loading states
    setIsLoadingFileProcessing(false);
    setIsLoadingOriginal(false);
    setIsLoadingTransformedData(false);
    setIsLoadingMappedData(false);
    setIsLoadingFinalData(false);
    setIsApplyingMappingsLocal(false);

    // Clear errors
    setFileProcessingError(null);
    setTransformError(null);
    setValidationErrors([]);

    // Reset model selection to default
    setSelectedModel(
      import.meta.env.DEV ? AI_MODELS.GPT_4O : AI_MODELS.CLAUDE_OPUS_4_1,
    );

    // Track reset event
    posthog.capture('workflow_reset', {
      previous_phase: currentPhase,
      had_files: selectedFiles.length > 0,
      had_data: originalData.length > 0,
    });
  }, [currentPhase, selectedFiles.length, originalData.length]);

  const hasOriginalData = originalData.length > 0;

  // Centralized busy flag for gating CTAs
  const isBusy =
    isLoadingFileProcessing ||
    isParsing ||
    isPreprocessing ||
    isMapping ||
    isApplyingMappings ||
    isGeneratingWorkflow ||
    isSubmitting ||
    isDetectingConstants ||
    isExtractingConstants;

  // Centralized logic for supplier rebate agreement validation
  // NOTE: We no longer check if agreements are "active" (not expired) based on today's date.
  // The backend validates that CSV line items fall within agreement date ranges.
  // This allows users to submit CSVs for expired agreements (e.g., historical data).
  const rebateAgreementValidation = useMemo(() => {
    const supplierId = String(
      (editedConstants as { supplierId?: string })?.supplierId ?? '',
    ).trim();
    const hasSupplier = supplierId.length > 0;
    const loaded = Array.isArray(supplierAgreements);

    if (!hasSupplier || !loaded) {
      return {
        hasFinalizedAgreements: false,
        showWarning: false,
        warningMessage: '',
        allAgreements: [],
        finalizedAgreements: [],
      };
    }

    // Get all finalized agreements (regardless of expiration date)
    const finalizedAgreements = supplierAgreements.filter(
      (a) => a.status === 'finalized',
    );

    // Check if any finalized agreements exist
    const hasFinalizedAgreements = finalizedAgreements.length > 0;

    // Show warning only if there are no finalized agreements at all
    const showWarning = !hasFinalizedAgreements;

    let warningMessage = '';
    if (supplierAgreements.length === 0) {
      // No agreements at all for this supplier
      warningMessage = supplierName
        ? `No rebate agreements found for supplier '${supplierName}'. Please create rebate agreements for this supplier before uploading CSV data.`
        : 'No rebate agreements found for this supplier. Please create rebate agreements for this supplier before uploading CSV data.';
    } else if (!hasFinalizedAgreements) {
      // Agreements exist but none are finalized
      const draftCount = supplierAgreements.filter(
        (a) => a.status === 'draft',
      ).length;
      warningMessage = supplierName
        ? `Found ${draftCount} rebate agreement(s) for supplier '${supplierName}', but they are all in draft status. CSV processing is only allowed for finalized agreements. Please finalize the relevant rebate agreement(s) before uploading CSV data.`
        : `Found ${draftCount} rebate agreement(s) for this supplier, but they are all in draft status. CSV processing is only allowed for finalized agreements. Please finalize the relevant rebate agreement(s) before uploading CSV data.`;
    }

    return {
      hasFinalizedAgreements,
      showWarning,
      warningMessage,
      allAgreements: supplierAgreements,
      finalizedAgreements,
    };
  }, [editedConstants, supplierAgreements, supplierName]);

  // Priority-based reasons array approach for consistency
  const getContinueDisabledReasons = (): string[] => {
    const reasons: string[] = [];

    // Highest priority: busy state (blocks everything)
    if (isBusy) {
      reasons.push('Processing in progress. Please wait to continue.');
      return reasons; // Return early - busy blocks everything
    }

    switch (currentPhase) {
      case 0:
        // Priority 1: Missing data (blocks validation)
        if (!originalData || originalData.length === 0) {
          if (isImageReviewMode) {
            reasons.push(
              'No data available. Please upload and parse image files to continue.',
            );
          } else {
            reasons.push(
              'No data available. Please upload and process your file to continue.',
            );
          }
          return reasons; // Return early - can't validate constants without data
        }

        // Priority 2: Missing constants
        if (!String(editedConstants?.supplierId ?? '').trim()) {
          reasons.push('Run the step and set the supplier to continue.');
        }

        // Priority 3: Missing currency
        if (!String(editedConstants?.currency ?? '').trim()) {
          reasons.push('Set the currency to continue.');
        }

        // Priority 4: No finalized rebate agreements
        if (
          !rebateAgreementValidation.hasFinalizedAgreements &&
          rebateAgreementValidation.warningMessage
        ) {
          reasons.push(rebateAgreementValidation.warningMessage);
        }

        break;

      case 1:
        if (!preprocessData) {
          reasons.push('No preprocessed data available.');
        }
        break;

      case 2:
        if (!mappingData) {
          reasons.push('No mapped data available.');
        } else if (hasUnappliedChanges) {
          reasons.push(
            'You have unapplied mapping changes. Click "Apply Changes" before continuing.',
          );
        } else if (hasUnknownInMapped) {
          reasons.push(
            'Resolve UNKNOWN values and run apply changes to continue.',
          );
        }
        break;

      case 3:
        if (!phaseThreeValidation.valid) {
          reasons.push(phaseThreeValidation.reason);
        }
        break;
    }

    return reasons;
  };

  const continueDisabledReasons = getContinueDisabledReasons();
  const canProceed = continueDisabledReasons.length === 0;
  const continueDisabledReason = continueDisabledReasons[0] || undefined;

  // Unified submit gating for Phase 3
  const canSubmitUnified =
    currentPhase === 3 &&
    !isBusy &&
    phaseThreeValidation.valid &&
    !hasUnappliedConstantChanges &&
    !hasUnappliedConversionRateChange;
  const submitDisabledReasonUnified =
    currentPhase === 3
      ? isBusy
        ? 'Processing in progress. Please wait to continue.'
        : hasUnappliedConstantChanges || hasUnappliedConversionRateChange
          ? 'You have unapplied changes. Please re-run the workflow or apply the conversion rate.'
          : !phaseThreeValidation.valid
            ? phaseThreeValidation.reason
            : undefined
      : undefined;

  // Derive a stable key for the current upload selection
  const uploadKey = useMemo(() => {
    return selectedFiles
      .map((f) => `${f.name}:${f.lastModified}:${f.size}`)
      .join('|');
  }, [selectedFiles]);

  // Check for duplicate files after original data is loaded
  const shouldCheckDuplicates =
    selectedFiles.length > 0 && originalData.length > 0 && currentPhase === 0;
  const firstFile = selectedFiles[0];

  const { data: duplicateCheckResult } = useCheckDuplicateDocuments({
    originalFileName: firstFile?.name || '',
    fileSizeBytes: firstFile?.size || 0,
    tolerancePercent: 1,
    enabled: shouldCheckDuplicates,
  });

  // Show duplicate warning modal when duplicates are detected
  useEffect(() => {
    if (
      duplicateCheckResult?.hasDuplicates &&
      duplicateCheckResult.duplicates.length > 0 &&
      currentPhase === 0 &&
      originalData.length > 0
    ) {
      setDuplicateInfo(duplicateCheckResult.duplicates);
      setDuplicateWarningOpen(true);

      // Track that we showed a duplicate warning
      posthog.capture('workflow_duplicate_file_warning_shown', {
        context: 'convert_data_page_workflow',
        file_name: firstFile?.name,
        file_size: firstFile?.size,
        duplicate_count: duplicateCheckResult.duplicates.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateCheckResult, currentPhase, firstFile]);

  // Handle continuing after duplicate warning
  const handleContinueAfterDuplicate = useCallback(() => {
    // Track that user chose to continue despite duplicate warning
    posthog.capture('workflow_duplicate_file_warning_overridden', {
      context: 'convert_data_page_workflow',
      file_name: firstFile?.name,
      file_size: firstFile?.size,
      duplicate_count: duplicateInfo.length,
    });

    // If there was a pending phase change, execute it now
    if (pendingPhaseChange !== null) {
      void changePhase(pendingPhaseChange as WorkflowPhase);
      setPendingPhaseChange(null);
    }
  }, [duplicateInfo.length, firstFile, pendingPhaseChange, changePhase]);

  // Auto-load file from pending upload queue when pendingUploadId is provided
  useEffect(() => {
    const loadFromPendingUpload = async () => {
      if (
        !pendingUploadId ||
        pendingUploadProcessed ||
        isLoadingPendingUpload
      ) {
        return;
      }

      setIsLoadingPendingUpload(true);
      setPendingUploadProcessed(true);

      try {
        // Start the pending upload (marks as processing and returns download URL)
        const result = await startPendingUpload({ id: pendingUploadId });
        if (!result.data) {
          throw new Error('No data returned from start pending upload');
        }
        const data = result.data as StartPendingUploadResponse;
        const { downloadUrl, originalFileName, mimeType } = data;

        // Fetch the file from the signed URL
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error('Failed to download file');
        }

        const blob = await response.blob();
        const file = new File([blob], originalFileName, { type: mimeType });

        // Process the file based on type
        const extension = originalFileName.split('.').pop()?.toLowerCase();

        if (extension === 'csv') {
          const text = await blob.text();
          setSelectedFiles([file]);
          processCSVData(text);
          void changePhase(0);
        } else if (extension === 'xlsx' || extension === 'xls') {
          const arrayBuffer = await blob.arrayBuffer();
          const binaryStr = new Uint8Array(arrayBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            '',
          );
          setSelectedFiles([file]);
          await processXLSXData(binaryStr);
          void changePhase(0);
        } else {
          showToast({
            title: 'Unsupported file type',
            message: `File type .${extension} is not supported`,
            color: 'error',
          });
        }

        showToast({
          title: 'File loaded',
          message: `Loaded ${originalFileName} from email queue`,
          color: 'success',
        });

        posthog.capture('workflow_loaded_from_pending_upload', {
          pendingUploadId,
          fileType: extension,
        });
      } catch (error) {
        console.error('Failed to load from pending upload:', error);
        showToast({
          title: 'Failed to load file',
          message: error instanceof Error ? error.message : 'Unknown error',
          color: 'error',
        });
        // Reset so user can retry
        setPendingUploadProcessed(false);
      } finally {
        setIsLoadingPendingUpload(false);
      }
    };

    void loadFromPendingUpload();
  }, [
    pendingUploadId,
    pendingUploadProcessed,
    isLoadingPendingUpload,
    startPendingUpload,
    processCSVData,
    processXLSXData,
    changePhase,
    showToast,
  ]);

  // Auto-trigger image parsing from parent to avoid duplicate child triggers
  useEffect(() => {
    const shouldAutoParse =
      currentPhase === 0 &&
      isImageReviewMode &&
      fileType === 'image' &&
      selectedFiles.length > 0 &&
      originalData.length === 0 &&
      !isParsing &&
      uploadKey &&
      !autoParsedUploads.has(uploadKey);

    if (shouldAutoParse) {
      autoParsedUploads.add(uploadKey);
      void parseImagesWithCustomInstructions(undefined);
    }
  }, [
    currentPhase,
    isImageReviewMode,
    fileType,
    selectedFiles.length,
    originalData.length,
    isParsing,
    uploadKey,
    parseImagesWithCustomInstructions,
  ]);

  return (
    <DashboardLayout>
      <div className="py-6 pr-5">
        {/* Optional Back Button */}
        {onBack && (
          <Button variant="ghost" onClick={onBack} className="mb-4">
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Upload Options
          </Button>
        )}

        <h1 className="text-2xl font-bold mb-4">
          Convert Data
          {selectedFiles.length > 0 &&
            ` - ${selectedFiles.map((f) => f.name).join(', ')}`}
        </h1>

        <div className="mt-4 flex flex-col flex-1">
          {/* Loading state for pending upload */}
          {isLoadingPendingUpload && (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner
                text="Loading file from email queue..."
                minHeight={300}
                size={80}
              />
            </div>
          )}

          {!hasOriginalData && !isImageReviewMode && !isLoadingPendingUpload ? (
            <>
              {/* OCR Processing Overlay */}
              {isParsing && selectedFiles.length > 0 && (
                <div className="relative">
                  <div className="absolute inset-0 bg-white bg-opacity-90 z-10 flex items-center justify-center rounded-lg">
                    <LoadingSpinner
                      text="Processing images with AI OCR..."
                      minHeight={200}
                      size={80}
                    />
                  </div>
                  {/* Content below overlay */}
                  <div className="opacity-50">
                    <FileUploadDropzone
                      onFilesSelected={handleFileUpload}
                      uploadProgress={[]}
                      isUploading={isLoadingFileProcessing || isParsing}
                      disabled={isLoadingFileProcessing || isParsing}
                      maxFiles={10}
                      fillAvailableHeight={!selectedFiles.length}
                      minHeight={selectedFiles.length > 0 ? 400 : 600}
                    />
                  </div>
                </div>
              )}

              {/* Normal Content - Show when not processing OCR */}
              {!isParsing && (
                <FileUploadDropzone
                  onFilesSelected={handleFileUpload}
                  uploadProgress={[]}
                  isUploading={isLoadingFileProcessing || isParsing}
                  disabled={isLoadingFileProcessing || isParsing}
                  maxFiles={10} // Allow multiple files, validation happens in handleFileUpload
                  fillAvailableHeight={!selectedFiles.length}
                  minHeight={selectedFiles.length > 0 ? 400 : 600}
                />
              )}

              {/* Show selected files and process button */}
              {selectedFiles.length > 0 && !isParsing && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h2 className="text-lg font-semibold mb-2">
                    Selected Files ({selectedFiles.length})
                  </h2>

                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between mb-2"
                    >
                      <p className="text-sm">
                        📄 {file.name} ({(file.size / 1024 / 1024).toFixed(2)}{' '}
                        MB)
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedFiles([])}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}

                  <div className="mt-4">
                    <Button
                      onClick={handleProcessFiles}
                      disabled={isLoadingFileProcessing || isParsing}
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isLoadingFileProcessing || isParsing
                        ? 'Processing...'
                        : 'Process Files'}
                    </Button>
                  </div>
                </div>
              )}

              {fileProcessingError && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-red-200">
                  <p className="text-sm text-red-600 mb-2">
                    <strong>Error processing file:</strong>{' '}
                    {fileProcessingError}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFileProcessingError(null);
                      setValidationErrors([]);
                      setSelectedFiles([]);
                    }}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    Try Again
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Navigation */}
              <LinearWorkflowNavigator
                currentPhase={currentPhase}
                onContinue={handleContinue}
                onBack={handleBack}
                onReset={resetWorkflow}
                hidePhaseZero={false}
                isLoading={isBusy}
                fileType={fileType}
                onSubmit={handleSubmitClick}
                isSubmitting={isSubmitting}
                canSubmit={canSubmitUnified}
                submitDisabledReason={submitDisabledReasonUnified}
                canProceed={canProceed}
                continueDisabledReason={continueDisabledReason}
              />

              {/* Phase Content */}
              {currentPhase === 0 && (
                <>
                  {/* Debug: ConvertDataPageWorkflow Phase 0 */}
                  <PhaseZeroSheetSelection
                    workbook={workbook}
                    selectedSheet={selectedSheet}
                    originalData={originalData}
                    isLoadingOriginal={isLoadingOriginal}
                    selectedFiles={selectedFiles}
                    onSheetChange={handleSheetChange}
                    onOriginalDataChange={setOriginalData}
                    fileType={fileType}
                    isImageReviewMode={isImageReviewMode}
                    onImageReparse={handleImageReparse}
                    isReparsing={isParsing}
                    constants={editedConstants}
                    onConstantsChange={setEditedConstants}
                    detectingConstants={
                      isDetectingConstants || isExtractingConstants
                    }
                    showNoFinalizedAgreementsWarning={
                      rebateAgreementValidation.showWarning
                    }
                    rebateAgreementWarningMessage={
                      rebateAgreementValidation.warningMessage
                    }
                    supplierAgreements={rebateAgreementValidation.allAgreements}
                  />
                </>
              )}

              {currentPhase === 1 && (
                <PhaseOnePreprocessing
                  originalData={originalData}
                  preprocessedData={preprocessedData}
                  isLoadingOriginal={isLoadingOriginal}
                  isLoadingPreprocessed={
                    isLoadingTransformedData || isPreprocessing
                  }
                  selectedFiles={selectedFiles}
                  transformError={transformError}
                  onOriginalDataChange={setOriginalData}
                  onPreprocessedDataChange={setPreprocessedData}
                  onConversationClear={clearErrors}
                  constants={editedConstants as WorkflowConstants}
                  supplierName={supplierName}
                  onConstantsChange={setEditedConstants}
                  workflowSteps={preprocessData?.data.workflowSteps}
                  rawWorkflowSteps={preprocessData?.data.rawWorkflowSteps}
                  isGeneratingWorkflow={isPreprocessing}
                  workflowSuccess={preprocessData?.success}
                  workflowError={preprocessingWorkflowError}
                  failedStepId={preprocessingFailedStepId}
                />
              )}

              {currentPhase === 2 && (
                <PhaseTwoMapping
                  ref={phaseTwoMappingRef}
                  preprocessedData={preprocessedData}
                  mappedData={mappedData}
                  constants={editedConstants as WorkflowConstants}
                  supplierName={supplierName}
                  onConstantsChange={setEditedConstants}
                  mappings={editedMappings}
                  onMappingsChange={setEditedMappings}
                  onApplyMappings={handleRefreshMappings}
                  onResetMappings={handleResetMappings}
                  isLoading={
                    isLoadingMappedData ||
                    isMapping ||
                    isApplyingMappings ||
                    isApplyingMappingsLocal
                  }
                  hasUnappliedChanges={hasUnappliedChanges}
                  error={transformError}
                  filename={selectedFiles[0]?.name || 'spreadsheet'}
                />
              )}

              {currentPhase === 3 && (
                <PhaseThreeWorkflow
                  mappedData={mappedData}
                  transformedData={transformedData}
                  isLoadingMapped={false}
                  isLoadingTransformed={
                    isLoadingFinalData || isGeneratingWorkflow
                  }
                  selectedFiles={selectedFiles}
                  transformError={transformError}
                  isGeneratingWorkflow={isGeneratingWorkflow}
                  workflowData={workflowData}
                  onMappedDataChange={setMappedData}
                  setTransformedData={setTransformedData}
                  constants={editedConstants as WorkflowConstants}
                  supplierName={supplierName}
                  onConversationClear={clearErrors}
                  workflowSuccess={workflowData?.success}
                  workflowError={workflowGenerationError}
                  failedStepId={workflowGenerationFailedStepId}
                  onApplyCurrencyConversion={handleApplyCurrencyConversion}
                  conversionApplied={conversionApplied}
                  onConstantsChange={setEditedConstants}
                  hasUnappliedConstantChanges={hasUnappliedConstantChanges}
                  hasUnappliedConversionRateChange={
                    hasUnappliedConversionRateChange
                  }
                  onResetConstants={handleResetConstants}
                  onConversionRateChange={handleConversionRateChange}
                  bannerError={bannerError}
                />
              )}
            </>
          )}
        </div>

        {/* AI Assistant v2 - Only show when files are processed */}
        {((currentPhase !== 0 && originalData.length > 0) ||
          (currentPhase === 0 && originalData.length > 0)) && (
          <AIAssistantChatV2
            ref={aiChatRef}
            currentPhase={currentPhase}
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            useTestMode={useTestMode}
            onTestModeChange={setUseTestMode}
            isTransforming={isBusy}
            onImageReparseRequest={handleImageReparse}
            onPreprocessingRequest={handlePreprocessing}
            onMappingRequest={handleFieldMapping}
            onWorkflowRequest={handleWorkflowGeneration}
            onClearConversation={handleClearConversation}
            isImageReviewMode={isImageReviewMode}
            // Inline flow controls mirrored from navigator
            canProceed={canProceed}
            continueDisabledReason={continueDisabledReason}
            onContinue={currentPhase === 3 ? handleSubmitClick : handleContinue}
            isSubmitting={isSubmitting}
            // Phase 3 submit gating (centralized from parent)
            canSubmit={canSubmitUnified}
            submitDisabledReason={submitDisabledReasonUnified}
            isBusy={isBusy}
          />
        )}
      </div>

      {/* Raw File Preview Footer with Popout */}
      {selectedFiles.length > 0 && (
        <RawFilePreviewPopout
          selectedFiles={selectedFiles}
          className="mt-6"
          selectedSheet={selectedSheet || undefined}
        />
      )}

      {/* Submit Confirmation Dialog */}
      <ConfirmDialog
        open={confirmSubmitOpen}
        onClose={() => setConfirmSubmitOpen(false)}
        onConfirm={handleSubmit}
        title="Submit Workflow Data"
        message="Are you sure you want to submit this transformed data?"
        confirmText="Submit"
        loading={isSubmitting || isCheckingOverlap}
      />

      {/* Overlap Warning Modal */}
      <OverlapWarningModal
        open={overlapModalOpen}
        onClose={() => {
          setOverlapModalOpen(false);
          pendingSubmissionRef.current = null;
        }}
        onResolve={handleOverlapResolve}
        overlappingInvoices={overlappingInvoices}
        totalAffectedLineItems={totalAffectedLineItems}
        isSubmitting={isSubmitting}
        supplierId={currentSupplierId}
      />

      {/* Duplicate File Warning Modal */}
      <DuplicateFileWarningModal
        open={duplicateWarningOpen}
        onClose={() => {
          setDuplicateWarningOpen(false);
          setPendingPhaseChange(null);
        }}
        onContinue={handleContinueAfterDuplicate}
        duplicates={duplicateInfo}
        fileName={firstFile?.name || ''}
      />
    </DashboardLayout>
  );
};

export default ConvertDataPageWorkflow;
